import React, { useState } from 'react';
import axios from 'axios';

const IssuingControls = ({ booking, onUpdate }) => {
  const [requestedLimit, setRequestedLimit] = useState(900);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const issueCard = async () => {
    setLoading(true);
    setMsg('Issuing card...');

    try {
      const response = await axios.post('/issuing/provision-for-booking', {
        booking_id: booking.id,
        requested_limit: requestedLimit * 100 // Convert to cents
      });
      
      setMsg(`✅ ${response.data.message}`);
      onUpdate();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Failed to issue card');
    } finally {
      setLoading(false);
    }
  };

  const deactivateCard = async () => {
    setLoading(true);
    setMsg('Deactivating card...');

    try {
      const response = await axios.post(`/issuing/deactivate-card/${booking.id}`);
      setMsg('✅ Card deactivated successfully');
      onUpdate();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Failed to deactivate card');
    } finally {
      setLoading(false);
    }
  };

  const calculateRecommendedLimit = () => {
    const count = booking.guest_count;
    if (count >= 5 && count <= 9) {
      return { min: 800, max: 950, default: 900 };
    } else if (count >= 10) {
      return { amount: count * 120, catering: true };
    } else {
      return { amount: count * 150 };
    }
  };

  const recommended = calculateRecommendedLimit();

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="issuing-controls">
      <h4 className="font-semibold mb-3">💳 Issuing / Geo-Fence Controls</h4>
      
      <div className="space-y-2 text-sm mb-4">
        <div className="flex justify-between items-center">
          <span>Guests:</span>
          <span className="font-bold text-lg">{booking.guest_count} guests</span>
        </div>
        <div className="flex justify-between items-center">
          <span>Recommended Grocery Limit:</span>
          <span className="font-bold text-green-600">
            {recommended.catering 
              ? `$${recommended.amount} (Catering: $120/guest)`
              : recommended.min 
                ? `$${recommended.min} - $${recommended.max}`
                : `$${recommended.amount} ($${(recommended.amount / booking.guest_count).toFixed(0)}/guest)`
            }
          </span>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
          <p className="text-xs text-yellow-800">
            ⚠️ <strong>Authorization must match guest count:</strong> This ensures proper grocery budget per person.
          </p>
        </div>
        <div className="border-t border-blue-300 pt-2 mt-2">
          <div>Service Area: {booking.service_city}, {booking.service_state} {booking.service_zip}</div>
          {booking.service_zip_allowlist && booking.service_zip_allowlist.length > 0 && (
            <div>ZIP Allowlist: {booking.service_zip_allowlist.join(', ')}</div>
          )}
        </div>
      </div>

      {!booking.issuing_card_id && (
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">
            Card Limit ($):
          </label>
          <input
            type="number"
            min={recommended.min || recommended.amount}
            max={recommended.max || recommended.amount}
            step="10"
            value={requestedLimit}
            onChange={(e) => setRequestedLimit(parseFloat(e.target.value))}
            placeholder={`Recommended: $${recommended.default || recommended.amount}`}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            data-testid="limit-input"
          />
          <p className="text-xs text-gray-600 mt-1">
            For {booking.guest_count} guests: ${(requestedLimit / booking.guest_count).toFixed(2)}/guest
          </p>
        </div>
      )}

      <div className="flex gap-3">
        {!booking.issuing_card_id ? (
          <button
            onClick={issueCard}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
            data-testid="issue-card-button"
          >
            {loading ? 'Issuing...' : 'Issue Grocery Card'}
          </button>
        ) : (
          <>
            <div className="text-sm text-green-700">
              ✅ Card issued (Limit: ${(booking.issuing_limit_cents / 100).toFixed(2)})
            </div>
            {!booking.card_deactivated_at && (
              <button
                onClick={deactivateCard}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                data-testid="deactivate-card-button"
              >
                Deactivate Card
              </button>
            )}
          </>
        )}
      </div>

      {msg && (
        <div className={`mt-3 text-sm ${
          msg.includes('✅') ? 'text-green-700' : 'text-red-700'
        }`} data-testid="issuing-message">
          {msg}
        </div>
      )}
    </div>
  );
};

export default IssuingControls;
